import type {
  AppliedPromotion,
  CheckoutData,
  LoyaltyRewardSelection,
  Promotion,
  Service,
} from "@lemmonhq/types";
import {
  calculateCheckoutSubTotal,
  calculateLegScopedRewards,
} from "../computation/checkout";
import { calculatePromotionApplications } from "./reward-calculation";
import { validateAllRequirements } from "./requirements-validation";
import {
  getCurrentTimeContext,
  getTimeContextForTableCheckout,
  isPromotionEligible,
} from "./time-utils";
import { getProductIdsFromCategories } from "./item-utils";

export const isLoyaltyPromotion = (p: Promotion) =>
  !!(p.coin_program_id || p.membership_program_id);
export type LoyaltyRewardReason =
  | "conditions"
  | "coins"
  | "allowance"
  | "membership_conflict"
  | "coin_conflict"
  | "choose_product"
  | "no_value";
export type LoyaltyRewardOption = {
  promotion: Promotion;
  applications: AppliedPromotion[];
  value: number;
  reason?: LoyaltyRewardReason;
};
const round = (v: number) => Math.round(v * 100) / 100;

export const normalizeLoyaltyRewardSelections = (
  selections: (string | LoyaltyRewardSelection)[] = [],
): LoyaltyRewardSelection[] => {
  const byPromotion = new Map<string, LoyaltyRewardSelection>();
  for (const selection of selections) {
    const normalized =
      typeof selection === "string"
        ? { promotion_id: selection, quantity: 1 }
        : {
            ...selection,
            quantity: Math.max(0, Math.floor(selection.quantity || 0)),
          };
    if (!normalized.promotion_id || normalized.quantity <= 0) continue;
    byPromotion.set(normalized.promotion_id, normalized);
  }
  return [...byPromotion.values()];
};

const isRepeatableCoinReward = (promotion: Promotion) =>
  !!promotion.coin_program_id &&
  ((promotion.reward.type === "order_discount" &&
    promotion.reward.mode === "absolute") ||
    promotion.reward.type === "product" ||
    promotion.reward.type === "product_category");

/** One evaluator for the loyalty sheet, promotion picker and checkout refresh.
 * Balances are advisory; payment still owns authoritative validation/capture.
 * One coin reward per program matches capture's (payment, program) deduplication.
 */
export function evaluateLoyaltySelection(
  services: Service[],
  checkout: CheckoutData,
  promotions: Promotion[],
  selectedIds: (string | LoyaltyRewardSelection)[],
  ordinary: AppliedPromotion[] = [],
) {
  const customer = checkout.customer;
  const normalizedSelections = normalizeLoyaltyRewardSelections(selectedIds);
  const selections = new Map(
    normalizedSelections.map((selection) => [
      selection.promotion_id,
      selection,
    ]),
  );
  const selected = new Set(selections.keys());
  const subtotal = calculateCheckoutSubTotal(checkout);
  const factor = checkout.staffCheckout?.intent
    ? subtotal > 0
      ? checkout.staffCheckout.intent.amount / subtotal
      : 0
    : 1;
  // Checkout stores whole-cart applications; payment scopes them to its leg.
  // Convert per-payment limits to that representation before capping.
  let base = Math.max(
    0,
    (factor > 0 ? subtotal - (checkout.discount?.amount ?? 0) / factor : 0) -
      ordinary.reduce((s, a) => s + a.value, 0),
  );
  let cappedSelected = false;
  const spentPrograms = new Set<string>();
  const applications: AppliedPromotion[] = [];
  const options = new Map<string, LoyaltyRewardOption>();
  const candidates = promotions.filter(
    (p) =>
      isLoyaltyPromotion(p) &&
      (p.membership_program_id
        ? customer?.membership_program_ids?.includes(p.membership_program_id)
        : !!customer && p.coin_program_id! in customer.coin_balances),
  );
  // Ordinary reductions first, then uncapped perks, then explicit finite rewards.
  const isCapped = (p: Promotion) =>
    customer?.memberships?.some(
      (m) =>
        m.program_id === p.membership_program_id &&
        (m.cap_count != null || m.cap_amount != null),
    );
  const ordered = [...candidates].sort(
    (a, b) =>
      Number(!selected.has(a.id)) - Number(!selected.has(b.id)) ||
      (isCapped(a) ? 2 : a.coin_program_id ? 1 : 0) -
        (isCapped(b) ? 2 : b.coin_program_id ? 1 : 0) ||
      a.id.localeCompare(b.id),
  );
  for (const p of ordered) {
    const m = customer?.memberships?.find(
      (m) => m.program_id === p.membership_program_id,
    );
    const coinRequirements = p.requirements.filter((r) => r.type === "coin");
    const requestedQuantity = selected.has(p.id)
      ? isRepeatableCoinReward(p)
        ? (selections.get(p.id)?.quantity ?? 1)
        : 1
      : 1;
    const time =
      checkout.payload?.type === "table"
        ? getTimeContextForTableCheckout(
            checkout.payload.object,
            p.requirements,
            (ids) => getProductIdsFromCategories(services, ids),
          )
        : getCurrentTimeContext();
    let reason: LoyaltyRewardReason | undefined;
    if (!isPromotionEligible(p, time)) reason = "conditions";
    else if (
      coinRequirements.some(
        (r) =>
          (customer?.coin_balances[r.coin_program_id] ?? 0) <
          r.amount * requestedQuantity,
      )
    )
      reason = "coins";
    else if (!validateAllRequirements(services, p.requirements, checkout))
      reason = "conditions";
    else if (
      m &&
      ((m.cap_count != null && (m.remaining ?? 0) <= 0) ||
        (m.cap_amount != null && (m.budget_remaining ?? 0) <= 0))
    )
      reason = "allowance";
    else if (isCapped(p) && cappedSelected) reason = "membership_conflict";
    else if (p.coin_program_id && spentPrograms.has(p.coin_program_id))
      reason = "coin_conflict";
    else if (p.reward.type === "product_category" && selected.has(p.id)) {
      const selectedItems =
        selections
          .get(p.id)
          ?.product_choices?.reduce(
            (sum, choice) => sum + Math.max(0, Math.floor(choice.quantity)),
            0,
          ) ?? 0;
      if (selectedItems !== p.reward.quantity * requestedQuantity)
        reason = "choose_product";
    }
    let apps: AppliedPromotion[] = [];
    let available = Math.min(
      base,
      m?.cap_amount != null
        ? (m.budget_remaining ?? 0) / (factor || 1)
        : Infinity,
    );
    if (!reason) {
      for (let unit = 1; unit <= requestedQuantity; unit++) {
        let unitApps = calculatePromotionApplications(services, p, checkout, {
          selection: selections.get(p.id),
          redemptionUnit: unit,
        });
        let unitAvailable = available;
        // Percentage membership discounts use the remaining merchandise base.
        if (
          p.reward.type === "order_discount" &&
          p.reward.mode === "percentage"
        )
          unitAvailable = Math.min(
            unitAvailable,
            round((base * p.reward.value) / 100),
          );
        unitApps = unitApps
          .map((a) => {
            const value = round(Math.max(0, Math.min(a.value, unitAvailable)));
            unitAvailable = round(unitAvailable - value);
            available = round(available - value);
            return {
              ...a,
              value,
              display: {
                ...a.display,
                reward: p.reward,
                redemption_unit: unit,
                redemption_units: requestedQuantity,
              },
            };
          })
          .filter((a) => a.value > 0);
        apps.push(...unitApps);
        if (available <= 0) break;
      }
    }
    const value = round(apps.reduce((s, a) => s + a.value, 0));
    if (
      !reason &&
      (value <= 0 ||
        (calculateLegScopedRewards({ ...checkout, promotions: apps })?.total ??
          0) <= 0) &&
      !(p.reward.type === "product_category" && !selected.has(p.id))
    )
      reason = "no_value";
    options.set(p.id, { promotion: p, applications: apps, value, reason });
    if (selected.has(p.id) && !reason) {
      applications.push(...apps);
      base = round(base - value);
      if (isCapped(p)) cappedSelected = true;
      if (p.coin_program_id) spentPrograms.add(p.coin_program_id);
    }
  }
  return {
    applications,
    options: candidates.map((p) => options.get(p.id)!),
    value: round(applications.reduce((s, a) => s + a.value, 0)),
  };
}
